import { memo, useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";

import { Box, Button, Grid } from "@mui/material";
import {
  setFormOptions,
  setFlyFromAndTo,
  setSearchFlightShopResult,
} from "../../../../../services/slices/addFlightShopSlice";
import { persistor } from "../../../../../store/store";
import Spinner from "../../../../../layout/spinner/spinner"
import CustomModal from "../../../../../components/common/modal/customModal";

import { isValue } from "../../../../../utils/constants/constants";
import { clearAllReduxStates } from "../../../../../utils/common-hooks/clearState";
import { setSourceData } from "../../../../../services/slices/sourceDataSlice";
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { setLogoutSession } from "../../../../../services/slices/authTokenSlice";
import { setFlightroute } from "../../../../../services/slices/addformSlice";
import CustomSearchField from "../flight-shop-form/CustomSearchField";
import { responseData } from "../../../../../utils/helpers/helper";

const SearchFlightShopForm = ({ isFromPreviousPage, isEditDataFetched, selVal, setSelVal, handleCloseBulkSection }) => {
  const { shopid } = useParams();
  const dispatch = useDispatch();

  const navigate = useNavigate();

  const { visualizeInfo } = useSelector((state) => (state?.authToken));

  const { airPorts } = useSelector((state) => state?.airports);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [modalContent, setModalContent] = useState(isValue?.emptyString);
  const [disableButton, setDisableButton] = useState(true);
  const [disableSwap, setDisableSwap] = useState(true);
  const [isSourcesFetched, setIsSourcesFetched] = useState(false);
  const myRoute = useSelector((state) => state.addShop.addFlightShop);
  const [errorMessage, setErrorMessage] = useState()
  const filteredAirports = airPorts?.filter(i => i?.cityiata !== null);
  const [route, setRoute] = useState({
    flyfrom: "",
    flyto: ""
  })

  const formattedAirports = [...new Set(filteredAirports?.flatMap(i => {
    const cityNameWithoutSpaces = i?.cityname?.replace(/ /g, '') || "";
    return [
      `${cityNameWithoutSpaces} - ${i?.airportiata}`,
      `${cityNameWithoutSpaces} - ${i?.cityiata}`
    ];
  }))];

  useEffect(() => {
    setDisableButton(!(myRoute?.flyFrom?.toString() && myRoute?.flyTo?.toString()));
    setDisableSwap(!(myRoute?.flyFrom?.toString() && myRoute?.flyTo?.toString()))
  }, [myRoute?.flyFrom, myRoute?.flyTo]);

  const fetchSources = useCallback(async (data) => {
    const { from, to } = data;
    if (from !== to) {
      const config = {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      };
      try {
        setLoading(true)
        const searchResultRes = await axios.get(
          `${process.env.REACT_APP_API_URL}${'sources'}`,
          config
        );
        const searchResult = responseData(searchResultRes);
        if (searchResultRes?.status === 200) {
          dispatch(setSourceData(searchResult))
          dispatch(setFlyFromAndTo({ flyFrom: from, flyTo: to }));
          setLoading(false);
          dispatch(setSearchFlightShopResult(searchResult));
          dispatch(
            setFormOptions({
              otas: searchResult?.source?.filter((res) => res?.sourceType === "OTA")?.map((i) => i?.source),
              brands: searchResult?.carrier?.map((i) => i?.source),
              otherSources: searchResult?.source?.filter((res) => res?.sourceType === "Metasource")?.map((i) => i?.source),
              gds: searchResult?.source?.filter((res) => res?.sourceType === "GDS")?.map((i) => i?.source)
            })
          );
          setIsSourcesFetched(true)
        }
      } catch (error) {
        setLoading(true)
        console.log(error);
        if (error?.response?.status === 401 && responseData(error?.response)?.message === "New login found.") {
          dispatch(setLogoutSession(true));
        } else if (error?.response?.status === 401) {
          clearAllReduxStates(dispatch)
          persistor.purge();
          sessionStorage.clear();
          localStorage.clear();
          navigate("/")
        }
      }
    }
    else if (from && to) {
      setOpenModal(true);
      setModalContent("Please enter unique 'From' and 'To' airports.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, shopid]);

  useEffect(() => {
    if (parseInt(shopid) && myRoute?.flyFrom && myRoute?.flyTo) {
      !isFromPreviousPage && !isSourcesFetched && isEditDataFetched && fetchSources({ from: myRoute?.flyFrom, to: myRoute?.flyTo });
    }
    if (myRoute.flyFrom !== "" && myRoute?.flyTo !== "" && selVal?.from === "" && selVal?.to === "") {
      setSelVal({
        from: myRoute?.flyFrom,
        to: myRoute?.flyTo
      })
    }
    //eslint-disable-next-line
  }, [myRoute.flyFrom, myRoute.flyTo]);

  // Submit for Origin and Destination
  const handleSubmit = (e) => {
    e.preventDefault();
    handleCloseBulkSection()
    const { flyFrom, flyTo } = myRoute;
    const isInvalidFrom = formattedAirports.includes(flyFrom);
    const isInvalidTo = formattedAirports.includes(flyTo);
    if ((route?.flyfrom !== myRoute?.flyFrom) && (route?.flyto !== myRoute?.flyTo)) {
      setErrorMessage((prev) => ({ ...prev, flyTo: "Destination is invalid", flyFrom: "Origin is invalid" }))

    } else if (route?.flyfrom !== myRoute?.flyFrom) {
      setErrorMessage((prev) => ({ ...prev, flyFrom: "Origin is invalid", flyTo: "" }))

    } else if (route?.flyto !== myRoute?.flyTo) {
      setErrorMessage((prev) => ({ ...prev, flyTo: "Destination is invalid", flyFrom: "" }))

    } else if (isInvalidFrom && isInvalidTo) {
      dispatch(setFlightroute({
        flyFrom: flyFrom,
        flyTo: flyTo,
      }))
      setErrorMessage()
      fetchSources({ from: myRoute?.flyFrom, to: myRoute?.flyTo })
    } else if (flyFrom === flyTo) {
      setOpenModal(true);
      setModalContent("Please enter unique 'From' and 'To' airports.");
      setErrorMessage()
    }
  };

  const swapAirline2 = () => {
    const { flyFrom, flyTo } = myRoute;
    setSelVal({
      from: flyTo,
      to: flyFrom
    })
    dispatch(setFlightroute({
      flyFrom: flyTo,
      flyTo: flyFrom,
    }))
  }

  // Origin and Destination Option values
  const airportOptions = airPorts?.filter(i => i?.cityiata !== null)?.map(data => ({
    label: `${data?.airportiata} ${data?.cityname} , ${data?.cityiata} ${data?.airportname}`,
    value: `${data?.cityname} - ${data?.airportiata}`
  }));

  const handleChange = (val, name) => {
    if (name === "flyfrom") {
      if (val?.value) {
        dispatch(setFlightroute({
          flyFrom: val.value,
          flyTo: myRoute?.flyTo,
        }))
      }
      setSelVal({
        flyFrom: val.value,
        flyTo: myRoute?.flyTo,
      })
    } else if (name === "flyto") {
      if (val?.value) {
        dispatch(setFlightroute({
          flyFrom: myRoute?.flyFrom,
          flyTo: val.value,
        }))
      }
      setSelVal({
        flyFrom: myRoute?.flyFrom,
        flyTo: val.value,
      })
    }
  }

  return (
    <Box>
      <form onSubmit={handleSubmit}>
        <Grid container columns={{ xs: 4, sm: 6, md: 12 }} className={!visualizeInfo ? "m-auto justyfy-center  customwidthrestrick " : "m-auto justyfy-center "} id="createMmarketResponsive">
          <Grid item xs={3} className="from-input-filed">
            <CustomSearchField data={{ id: "flight", name: "flyfrom", placeholder: "Select origin", options: airportOptions, selectedVal: selVal?.from }} handleChange={handleChange} setDisableButton={setDisableButton} formattedAirports={formattedAirports} setRoute={setRoute} />
            <span className="err-txt w-100 d-block text-left pl-5 ">{errorMessage?.flyFrom || ""}</span>
          </Grid>
          <Grid item xs={1}>
            <div onClick={disableSwap ? "" : swapAirline2} className={disableSwap ? "disabled-btn-pointer add-swap-btn btn-swap-css" : "add-swap-btn btn-swap-css"}><SwapHorizIcon /></div>
          </Grid>
          <Grid item xs={3} className="to-input-filed">
            <CustomSearchField data={{ id: "flight", name: "flyto", placeholder: "Select Destination", options: airportOptions, selectedVal: selVal?.to }} handleChange={handleChange} setDisableButton={setDisableButton} formattedAirports={formattedAirports} setRoute={setRoute} />
            <span className="err-txt w-100 d-block text-left pl-5 ">{errorMessage?.flyTo || ""}</span>
          </Grid>
          <Grid item xs={1}>
            <Button variant={"contained"} type="submit" disabled={disableButton} className="secondary_button">Search</Button>
          </Grid>
        </Grid>
      </form>
      {loading && <Spinner />}
      <CustomModal
        open={openModal}
        handleClose={() => setOpenModal(!openModal)}
        lable={{ labelOne: "close", labelTwo: "ok" }}
        content={[{ header: "Warning" }, { modalContent: modalContent }]}
        handleClickOk={() => setOpenModal(false)}
      />
    </Box>
  );
};

export default memo(SearchFlightShopForm);
